import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Box,
  Typography,
  Button,
  TablePagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { perfilAcessoService, PerfilAcesso } from '../../services/perfilAcesso';

const PerfilAcessoTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Buscar todos os perfis de acesso
  const { data: perfis = [], isLoading, error } = useQuery({
    queryKey: ['perfisAcesso'],
    queryFn: perfilAcessoService.getAll,
  });

  // Debug: Log dos dados recebidos
  React.useEffect(() => {
    console.log('Perfis recebidos:', perfis);
    console.log('isLoading:', isLoading);
    console.log('error:', error);
  }, [perfis, isLoading, error]);

  // Filtrar perfis baseado na busca
  const filteredPerfis = useMemo(() => {
    console.log('Filtrando perfis. Total:', perfis.length, 'Search:', search);
    
    if (!search.trim()) {
      return perfis;
    }
    
    const filtered = perfis.filter(perfil =>
      (perfil.name?.toLowerCase().includes(search.toLowerCase()) || false) ||
      (perfil.id?.toLowerCase().includes(search.toLowerCase()) || false)
    );
    
    console.log('Resultado do filtro:', filtered.length);
    return filtered;
  }, [perfis, search]);

  // Paginação dos dados filtrados
  const paginatedPerfis = useMemo(() => {
    const paginated = filteredPerfis.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
    console.log('Dados paginados:', paginated.length, 'Página:', page, 'Linhas por página:', rowsPerPage);
    return paginated;
  }, [filteredPerfis, page, rowsPerPage]);

  const handleChangePage = (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEdit = (id: string) => {
    navigate(`/perfis-acesso/editar/${id}`);
  };

  const handleView = (id: string) => {
    navigate(`/perfis-acesso/visualizar/${id}`);
  };

  const handleNew = () => {
    navigate('/perfis-acesso/novo');
  };

  if (isLoading) {
    console.log('Renderizando estado de loading...');
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error) {
    console.log('Renderizando estado de erro:', error);
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar os perfis de acesso.</Typography>
      </Box>
    );
  }

  console.log('Renderizando tabela com dados. Total de perfis:', perfis.length);

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Filtrar"
          variant="outlined"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          slotProps={{
            input: {
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            },
          }}
          sx={{ minWidth: 300 }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleNew}
          sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
        >
          Novo Perfil
        </Button>
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>ID</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome do Perfil</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Nome Normalizado</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedPerfis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhum perfil encontrado para o filtro informado.' : 'Nenhum perfil cadastrado.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedPerfis.map((perfil) => (
                <TableRow key={perfil.id} hover>
                  <TableCell>{perfil.id}</TableCell>
                  <TableCell>{perfil.name}</TableCell>
                  <TableCell>{perfil.normalizedName || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="info"
                      onClick={() => handleView(perfil.id)}
                      size="small"
                      title="Visualizar"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(perfil.id)}
                      size="small"
                      title="Editar"
                    >
                      <EditIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Paginação */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredPerfis.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por página:"
        labelDisplayedRows={({ from, to, count }) => 
          `${from}-${to} de ${count} itens`
        }
        sx={{ 
          borderTop: '1px solid',
          borderColor: 'divider',
          '.MuiTablePagination-toolbar': {
            pl: 2,
            pr: 2
          }
        }}
      />
    </Paper>
  );
};

export default PerfilAcessoTable;
